/**
 * Classroom Page - ISOCNTRL
 * Course/lesson view: lesson list on left, video+content on right
 * Inspired by school-style LMS layout
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, CheckCircle2, Circle, Play, FileText,
  Clock, ChevronRight, BookOpen, Eye, FastForward,
  Lock, Award, Download
} from 'lucide-react';
import { toast } from 'sonner';
import LessonQuiz from '../components/LessonQuiz';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function ProgressBar({ percent }) {
  const color = percent >= 75 ? 'bg-green-500' : percent >= 25 ? 'bg-yellow-500' : 'bg-[#3b82f6]';
  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div className={`h-2.5 rounded-full transition-all ${color}`} style={{ width: `${percent}%` }}>
        <span className="sr-only">{percent}%</span>
      </div>
    </div>
  );
}

function getEmbedUrl(url) {
  if (!url) return '';
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  // Loom
  const loomMatch = url.match(/loom\.com\/share\/([\w-]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  // Already an embed URL or direct link
  return url;
}

export default function ClassroomPage() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const [path, setPath] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [watchStats, setWatchStats] = useState(null);
  const [finalExam, setFinalExam] = useState(null);
  const [showFinalExam, setShowFinalExam] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [certData, setCertData] = useState(null);
  const watchIntervalRef = useRef(null);
  const watchStartRef = useRef(null);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [pathsRes, lessonsRes, examRes] = await Promise.all([
        fetch(`${API_BASE}/api/elearning/paths`, { headers: getHeaders() }),
        fetch(`${API_BASE}/api/elearning/paths/${pathId}/lessons`, { headers: getHeaders() }),
        fetch(`${API_BASE}/api/elearning/paths/${pathId}/final-exam`, { headers: getHeaders() })
      ]);

      if (pathsRes.ok) {
        const allPaths = await pathsRes.json();
        const currentPath = allPaths.find(p => p.id === pathId);
        setPath(currentPath);
      }

      if (lessonsRes.ok) {
        const lessonsData = await lessonsRes.json();
        setLessons(lessonsData);
        const firstUncompleted = lessonsData.find(l => !l.completed);
        setSelectedLesson(firstUncompleted || lessonsData[0] || null);
      }

      if (examRes.ok) {
        const examData = await examRes.json();
        setFinalExam(examData);
      }

      // Check for existing certification
      const certsRes = await fetch(`${API_BASE}/api/elearning/certifications`, { headers: getHeaders() });
      if (certsRes.ok) {
        const certs = await certsRes.json();
        const pathCert = certs.find(c => c.path_id === pathId);
        if (pathCert) setCertData(pathCert);
      }
    } catch (err) {
      console.error('Failed to load classroom data:', err);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [pathId, getHeaders]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMarkComplete = async (lessonId) => {
    try {
      const lesson = lessons.find(l => l.id === lessonId);
      const endpoint = lesson?.completed ? 'uncomplete' : 'complete';
      const res = await fetch(`${API_BASE}/api/elearning/lessons/${lessonId}/${endpoint}`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        setLessons(prev => prev.map(l =>
          l.id === lessonId ? { ...l, completed: !l.completed } : l
        ));
        // Update path progress
        if (path) {
          const newCompleted = lessons.filter(l =>
            l.id === lessonId ? !l.completed : l.completed
          ).length;
          setPath(prev => ({
            ...prev,
            completed_lessons: newCompleted,
            progress_pct: Math.round((newCompleted / prev.total_lessons) * 100)
          }));
        }
        toast.success(lesson?.completed ? 'Lesson unmarked' : 'Lesson completed!');
      }
    } catch { toast.error('Failed to update'); }
  };

  // Load quiz and watch stats when lesson changes
  useEffect(() => {
    if (!selectedLesson) return;
    const loadLessonExtras = async () => {
      try {
        const [quizRes, statsRes] = await Promise.all([
          fetch(`${API_BASE}/api/elearning/lessons/${selectedLesson.id}/quiz`, { headers: getHeaders() }),
          fetch(`${API_BASE}/api/elearning/lessons/${selectedLesson.id}/watch-stats`, { headers: getHeaders() })
        ]);
        if (quizRes.ok) {
          const qData = await quizRes.json();
          setQuiz(qData);
        } else {
          setQuiz(null);
        }
        if (statsRes.ok) setWatchStats(await statsRes.json());
      } catch { /* silent */ }
    };
    loadLessonExtras();

    // Start watch time tracking heartbeat
    watchStartRef.current = Date.now();
    if (watchIntervalRef.current) clearInterval(watchIntervalRef.current);
    watchIntervalRef.current = setInterval(() => {
      sendWatchEvent('heartbeat');
    }, 30000); // every 30s

    // Send initial play event
    sendWatchEvent('play');

    return () => {
      if (watchIntervalRef.current) clearInterval(watchIntervalRef.current);
      sendWatchEvent('pause');
    };
  }, [selectedLesson?.id]);

  const sendWatchEvent = async (eventType) => {
    if (!selectedLesson) return;
    const elapsed = watchStartRef.current ? Math.round((Date.now() - watchStartRef.current) / 1000) : 0;
    if (eventType === 'heartbeat') {
      watchStartRef.current = Date.now(); // reset for next interval
    }
    try {
      await fetch(`${API_BASE}/api/elearning/lessons/${selectedLesson.id}/watch-event`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: eventType,
          current_time: 0,
          duration: (selectedLesson.duration_minutes || 0) * 60,
          elapsed: eventType === 'heartbeat' ? 30 : elapsed
        })
      });
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!path) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground mb-4">Course not found</p>
        <Button onClick={() => navigate('/elearning')} variant="outline">Back to E-Learning</Button>
      </div>
    );
  }

  const completedCount = lessons.filter(l => l.completed).length;
  const progressPct = lessons.length > 0 ? Math.round((completedCount / lessons.length) * 100) : 0;
  const allLessonsComplete = completedCount >= lessons.length && lessons.length > 0;

  // Sequential locking: a lesson is accessible only if all previous lessons are completed
  const isLessonAccessible = (idx) => {
    if (idx === 0) return true;
    return lessons.slice(0, idx).every(l => l.completed);
  };

  const handleFinalExamComplete = (result) => {
    if (result.passed) {
      setShowFinalExam(false);
      setCertData({ certification_title: result.certification_title });
      setShowCelebration(true);
      loadData(); // Refresh to get cert
    }
  };

  const handleDownloadCert = async () => {
    if (!certData?.id) {
      // Refetch certs to get ID
      const certsRes = await fetch(`${API_BASE}/api/elearning/certifications`, { headers: getHeaders() });
      if (certsRes.ok) {
        const certs = await certsRes.json();
        const pathCert = certs.find(c => c.path_id === pathId);
        if (pathCert) {
          setCertData(pathCert);
          downloadCertPdf(pathCert.id);
          return;
        }
      }
      toast.error('Certificate not found');
      return;
    }
    downloadCertPdf(certData.id);
  };

  const downloadCertPdf = async (certId) => {
    try {
      const res = await fetch(`${API_BASE}/api/elearning/certifications/${certId}/download`, { headers: getHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate_${path.certification_title || 'completion'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!');
    } catch {
      toast.error('Failed to download certificate');
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="classroom-page">
      {/* Celebration Modal */}
      {showCelebration && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" data-testid="celebration-modal">
          <div className="bg-white rounded-2xl p-10 max-w-lg mx-4 text-center shadow-2xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Congratulations!</h2>
            <p className="text-lg text-muted-foreground mb-1">You are now a</p>
            <p className="text-xl font-bold text-[#3b82f6] mb-6">
              Certified {certData?.certification_title || path.certification_title}
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              You have successfully completed all training modules and passed the final assessment.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => setShowCelebration(false)} data-testid="close-celebration">
                Close
              </Button>
              <Button onClick={handleDownloadCert} className="bg-[#3b82f6] hover:bg-[#2563eb]" data-testid="download-cert-btn">
                <Download className="h-4 w-4 mr-2" />
                Download Certificate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="flex items-center justify-between pb-4 border-b mb-4 shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/elearning')} data-testid="back-to-elearning">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="h-6 w-px bg-border" />
          <h1 className="font-bold text-lg">{path.title}</h1>
          {certData && (
            <Badge className="bg-green-500 text-white gap-1">
              <Award className="h-3 w-3" />
              Certified
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">{completedCount}/{lessons.length} lessons complete</span>
          <div className="w-32">
            <ProgressBar percent={progressPct} />
          </div>
          <Badge className="bg-[#3b82f6] text-white">{progressPct}%</Badge>
          {certData && (
            <Button size="sm" variant="outline" onClick={handleDownloadCert} data-testid="topbar-download-cert">
              <Download className="h-3.5 w-3.5 mr-1" /> Certificate
            </Button>
          )}
        </div>
      </div>

      {/* Main Content: Sidebar + Content */}
      <div className="flex flex-1 gap-6 min-h-0">
        {/* Left Sidebar */}
        <div className="w-80 shrink-0 overflow-y-auto border rounded-lg bg-card" data-testid="lesson-sidebar">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">{path.title}</h2>
            {path.certification_title && (
              <p className="text-[10px] text-[#3b82f6] mt-1">Certification: {path.certification_title}</p>
            )}
            <ProgressBar percent={progressPct} className="mt-2" />
          </div>
          <div className="divide-y">
            {lessons.map((lesson, idx) => {
              const isSelected = selectedLesson?.id === lesson.id;
              const accessible = isLessonAccessible(idx);
              return (
                <button
                  key={lesson.id}
                  onClick={() => accessible && setSelectedLesson(lesson)}
                  disabled={!accessible}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                    !accessible ? 'opacity-40 cursor-not-allowed' :
                    isSelected ? 'bg-amber-50 border-l-4 border-[#3b82f6]' : 'hover:bg-accent/50 border-l-4 border-transparent'
                  }`}
                  data-testid={`lesson-item-${lesson.id}`}
                >
                  <div className="mt-0.5 shrink-0">
                    {lesson.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : !accessible ? (
                      <Lock className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isSelected ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {lesson.title}
                    </p>
                    {lesson.duration_minutes > 0 && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Clock className="h-3 w-3" />
                        {lesson.duration_minutes} min
                      </span>
                    )}
                  </div>
                </button>
              );
            })}

            {/* Final Exam entry in sidebar */}
            {finalExam && (
              <button
                onClick={() => { if (allLessonsComplete) { setShowFinalExam(true); setSelectedLesson(null); } }}
                disabled={!allLessonsComplete}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                  !allLessonsComplete ? 'opacity-40 cursor-not-allowed' :
                  showFinalExam ? 'bg-amber-50 border-l-4 border-[#3b82f6]' : 'hover:bg-accent/50 border-l-4 border-transparent'
                }`}
                data-testid="final-exam-sidebar"
              >
                <div className="mt-0.5 shrink-0">
                  {(finalExam.user_passed || certData) ? (
                    <Award className="h-5 w-5 text-green-500" />
                  ) : !allLessonsComplete ? (
                    <Lock className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <FileText className="h-5 w-5 text-[#3b82f6]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${showFinalExam ? 'text-foreground' : 'text-muted-foreground'}`}>
                    Final Assessment
                  </p>
                  <span className="text-[10px] text-muted-foreground">
                    {certData ? 'Passed' : !allLessonsComplete ? 'Complete all lessons first' : 'Ready to take'}
                  </span>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1 overflow-y-auto" data-testid="lesson-content">
          {showFinalExam ? (
            /* Final Exam View */
            <FinalExamView
              pathId={pathId}
              finalExam={finalExam}
              certData={certData}
              onComplete={handleFinalExamComplete}
              onDownloadCert={handleDownloadCert}
              getHeaders={getHeaders}
            />
          ) : selectedLesson ? (
            <div>
              {/* Lesson Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">{selectedLesson.title}</h2>
                <button
                  onClick={() => handleMarkComplete(selectedLesson.id)}
                  className="flex items-center gap-2"
                  data-testid="mark-complete-btn"
                >
                  {selectedLesson.completed ? (
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                  ) : (
                    <Circle className="h-6 w-6 text-muted-foreground hover:text-green-500 transition-colors" />
                  )}
                </button>
              </div>

              {/* Video Player */}
              {selectedLesson.content_type === 'video' && selectedLesson.video_url && (
                <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden mb-6 shadow-lg">
                  <iframe
                    src={getEmbedUrl(selectedLesson.video_url)}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={selectedLesson.title}
                  />
                  {selectedLesson.duration_minutes > 0 && (
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {selectedLesson.duration_minutes}:00
                    </div>
                  )}
                </div>
              )}

              {/* Text Content */}
              {selectedLesson.text_content && (
                <div className="prose prose-sm max-w-none">
                  {selectedLesson.text_content.split('\n').map((paragraph, i) => (
                    <p key={i} className="mb-3 text-foreground leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}

              {/* Watch Stats */}
              {watchStats && watchStats.total_watch_seconds > 0 && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-4 p-3 bg-accent/30 rounded-lg" data-testid="watch-stats">
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    Watch time: {Math.round(watchStats.total_watch_seconds / 60)} min
                  </span>
                  {watchStats.seek_count > 0 && (
                    <span className="flex items-center gap-1">
                      <FastForward className="h-3.5 w-3.5" />
                      Seeks: {watchStats.seek_count}
                    </span>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    const idx = lessons.findIndex(l => l.id === selectedLesson.id);
                    if (idx > 0) setSelectedLesson(lessons[idx - 1]);
                  }}
                  disabled={lessons.findIndex(l => l.id === selectedLesson.id) === 0}
                  data-testid="prev-lesson-btn"
                >
                  Previous Lesson
                </Button>

                {!selectedLesson.completed && (
                  <Button
                    onClick={() => handleMarkComplete(selectedLesson.id)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="complete-and-next-btn"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark Complete & Continue
                  </Button>
                )}

                {(() => {
                  const idx = lessons.findIndex(l => l.id === selectedLesson.id);
                  const isLast = idx === lessons.length - 1;
                  if (isLast && selectedLesson.completed && finalExam && !certData) {
                    return (
                      <Button
                        onClick={() => { setShowFinalExam(true); setSelectedLesson(null); }}
                        className="bg-[#3b82f6] hover:bg-[#2563eb]"
                        data-testid="take-final-exam-btn"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Take Final Assessment
                      </Button>
                    );
                  }
                  return (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!isLast) setSelectedLesson(lessons[idx + 1]);
                      }}
                      disabled={isLast}
                      data-testid="next-lesson-btn"
                    >
                      Next Lesson
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Play className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>Select a lesson to begin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Final Exam View Component
 */
function FinalExamView({ pathId, finalExam, certData, onComplete, onDownloadCert, getHeaders }) {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (certData) {
    return (
      <div className="text-center py-16" data-testid="exam-passed-view">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Award className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Assessment Passed!</h2>
        <p className="text-muted-foreground mb-6">
          You are a Certified {certData.certification_title || finalExam?.certification_title || 'Specialist'}
        </p>
        <Button onClick={onDownloadCert} className="bg-[#3b82f6] hover:bg-[#2563eb]">
          <Download className="h-4 w-4 mr-2" /> Download Certificate
        </Button>
      </div>
    );
  }

  const questions = finalExam?.questions || [];

  const handleSelect = (qIdx, optIdx) => {
    if (results) return;
    setSelectedAnswers(prev => ({ ...prev, [qIdx]: optIdx }));
  };

  const handleSubmit = async () => {
    const answers = questions.map((_, i) => selectedAnswers[i] ?? -1);
    if (answers.some(a => a === -1)) {
      toast.error('Please answer all questions');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/elearning/paths/${pathId}/final-exam/submit`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data);
        if (data.passed) {
          toast.success(`Passed! Score: ${data.score_pct}%`);
          setTimeout(() => onComplete(data), 1500);
        } else {
          toast.error(`Score: ${data.score_pct}%. Need ${data.passing_score}% to pass.`);
        }
      }
    } catch {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setSelectedAnswers({});
    setResults(null);
  };

  const allAnswered = questions.every((_, i) => selectedAnswers[i] !== undefined);

  return (
    <div data-testid="final-exam-view">
      <div className="flex items-center gap-3 mb-6">
        <Award className="h-6 w-6 text-[#3b82f6]" />
        <div>
          <h2 className="text-xl font-bold">Final Assessment</h2>
          <p className="text-sm text-muted-foreground">Complete this assessment to earn your certification</p>
        </div>
      </div>

      {results && (
        <div className={`p-4 rounded-lg mb-6 ${results.passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            {results.passed
              ? <CheckCircle2 className="h-5 w-5 text-green-600" />
              : <></>
            }
            <span className="font-semibold">{results.passed ? 'Passed!' : 'Not Passed'}</span>
          </div>
          <p className="text-sm">
            Score: <strong>{results.score_pct}%</strong> ({results.earned_points}/{results.total_points} points)
            {!results.passed && <span className="text-muted-foreground"> — Need {results.passing_score}% to pass</span>}
          </p>
        </div>
      )}

      <div className="space-y-6">
        {questions.map((q, qIdx) => {
          const resultItem = results?.results?.[qIdx];
          return (
            <div key={qIdx} className="space-y-2" data-testid={`final-q-${qIdx}`}>
              <p className="font-medium text-sm">
                <span className="text-muted-foreground mr-2">{qIdx + 1}.</span>
                {q.question}
              </p>
              <div className="space-y-1.5 pl-5">
                {q.options.map((opt, oIdx) => {
                  const isSelected = selectedAnswers[qIdx] === oIdx;
                  let optClass = 'border rounded-lg px-4 py-2.5 text-sm cursor-pointer transition-all flex items-center gap-2';
                  if (results) {
                    if (oIdx === resultItem?.correct_index) {
                      optClass += ' bg-green-50 border-green-400 text-green-800';
                    } else if (isSelected && !resultItem?.correct) {
                      optClass += ' bg-red-50 border-red-400 text-red-800';
                    } else {
                      optClass += ' border-gray-200 text-muted-foreground';
                    }
                  } else {
                    optClass += isSelected
                      ? ' border-[#3b82f6] bg-blue-50 text-[#3b82f6]'
                      : ' border-gray-200 hover:border-gray-300 hover:bg-accent/30';
                  }
                  return (
                    <button key={oIdx} className={optClass} onClick={() => handleSelect(qIdx, oIdx)}>
                      <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 text-[10px] font-bold">
                        {String.fromCharCode(65 + oIdx)}
                      </span>
                      <span>{opt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-3 mt-8 pt-4 border-t">
        {results && !results.passed ? (
          <Button variant="outline" onClick={handleRetry} data-testid="final-retry-btn">
            Retry Assessment
          </Button>
        ) : !results ? (
          <Button
            onClick={handleSubmit}
            disabled={!allAnswered || submitting}
            className="bg-[#3b82f6] hover:bg-[#2563eb]"
            data-testid="final-submit-btn"
          >
            {submitting ? 'Submitting...' : 'Submit Assessment'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
